import { Component, OnInit, TemplateRef, viewChild, inject } from '@angular/core';
import { Process } from '../../data/process';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  BrowserMessagesService,
  ExportService,
  ModalRef,
  ModalService,
  UserService,
  LoaderComponent, SideMenuHeadlineComponent
} from '@rbb/ng-web-template-lib';
import { ProcessService } from '../../services/process.service';
import { EmployeeService } from '../../services/employee.service';
import { Employee } from '../../data/employee';
import { Approved } from '../../types/approved';
import { Approval } from '../../data/approval';
import { WebAuthService } from '@rbb/ng-web-auth-lib';
import { BodyApproval } from '../../body/body-approval';
import { FormService } from '../../services/form.service';
import { Form } from '../../data/form';
import { ApproverFormData } from '../../custom-field-parameter/approver-form-data';
import { ApproverDataToJson } from '../../data-to-json/approver-data-to-json';
import { BodyProcessStatus } from '../../body/body-process-status';
import { ProcessStatus } from '../../types/process-status';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BodyResubmit } from '../../body/body-resubmit';
import { BodyProcessAttachment } from '../../body/body-process-attachment';
import { ApproverType } from '../../types/approver-type';
import { ActionType } from '../../types/action-type';
import { FormsModule } from '@angular/forms';
import { ApproverListUserViewComponent } from '../approver-list-user-view/approver-list-user-view.component';
import { ProcessPreviewComponent } from '../process-preview/process-preview.component';
import { FormComponent } from '../form/form.component';
import { PrivacyPolicyComponent } from '../privacy-policy/privacy-policy.component';

@Component({
  selector: 'app-process-view',
  templateUrl: './process-view.component.html',
  styleUrls: ['./process-view.component.css'],
  imports: [FormsModule, ApproverListUserViewComponent, ProcessPreviewComponent, FormComponent, RouterLink, PrivacyPolicyComponent, LoaderComponent, SideMenuHeadlineComponent]
})
export class ProcessViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private modalService = inject(ModalService);
  webAuthService = inject(WebAuthService);
  private browserMessagesService = inject(BrowserMessagesService);
  private processService = inject(ProcessService);
  private employeeService = inject(EmployeeService);
  private formService = inject(FormService);
  private userService = inject(UserService);
  private router = inject(Router);
  private exportService = inject(ExportService);

  process: Process | undefined;
  employee: Employee | undefined;
  form: Form | undefined;
  isActiveApproval: Approval[] = [];

  decline = false;
  edit = false;
  comment = '';
  resubmit = '';

  id: string | undefined;

  readonly ngModalApprove = viewChild<TemplateRef<unknown>>('ngModalApprove');
  ngModalServiceApprove: ModalRef<unknown> | undefined;

  readonly ngModalResubmit = viewChild<TemplateRef<unknown>>('ngModalResubmit');
  ngModalServiceResubmit: ModalRef<unknown> | undefined;

  readonly ngModalOperator = viewChild<TemplateRef<unknown>>('ngModalOperator');
  ngModalServiceOperator: ModalRef<unknown> | undefined;

  readonly ngModalReject = viewChild<TemplateRef<unknown>>('ngModalReject');
  ngModalServiceReject: ModalRef<unknown> | undefined;

  readonly ngModalAttachment = viewChild<TemplateRef<unknown>>('ngModalAttachment');
  ngModalServiceAttachment: ModalRef<unknown> | undefined;

  readonly ngModalCancel = viewChild<TemplateRef<unknown>>('ngModalCancel');
  ngModalServiceCancel: ModalRef<unknown> | undefined;

  readonly ngModal = viewChild<TemplateRef<unknown>>('ngModal');
  ngModalService: ModalRef<unknown> | undefined;

  readonly ngModalPrivacyPolicy = viewChild<TemplateRef<unknown>>('ngModalPrivacyPolicy');
  ngModalServicePrivacyPolicy: ModalRef<unknown> | undefined;

  onlyRelevantFields = false;

  fileName: string | undefined;
  mimeType: string | undefined;
  data: string | undefined;
  fileError: string | undefined;

  constructor() {
    this.onlyRelevantFields = localStorage.getItem('rbbForms-onlyRelevantFields') === 'true';
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.params['id'];
    this.getProcess();
    this.getMyData();
  }

  getMyData(): void {
    this.employeeService.getMyApprovers().subscribe({
      next: (next) => {
        this.employee = next;
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Mitarbeiterdaten konnten nicht geladen werden');
      }
    });
  }

  saveOnlyRelevantFields(): void {
    localStorage.setItem('rbbForms-onlyRelevantFields', String(this.onlyRelevantFields));
  }

  getProcess(): void {
    if (this.id) {
      this.processService.getProcess(this.id).subscribe({
        next: (next) => {
          if (next.fields) {
            for (const field of next.fields) {
              if (field.formField) {
                field.formField.fieldCustomParameter = JSON.parse(field.formField.definition ?? '{}');
              }
            }
            this.process = next;
            if (this.process.number) {
              document.title = document.title.replace(environment.title, environment.title + ' - Antrag Nr. ' + this.process.number);
            } else if (this.process.status === 'DRAFT') {
              document.title = document.title.replace(environment.title, environment.title + ' - Antragsentwurf');
            } else if (this.process.status === 'OLD') {
              document.title = document.title.replace(environment.title, environment.title + ' - veralteter Entwurf');
            } else {
              document.title = document.title.replace(environment.title, environment.title + ' - Antrag');
            }
            this.checkActiveApproval();
          }
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht geladen werden');
        }
      });
    }
  }

  getForm(): void {
    if (this.process?.formId) {
      this.formService.getForm(this.process.formId).subscribe({
        next: (next) => {
          this.form = next;
          const tmpApproverData: ApproverFormData[] = this.form.approverData ? JSON.parse(this.form.approverData) : [];
          this.form.approverDataCustomParameter = new Array<ApproverDataToJson>(tmpApproverData.length);
          for (let i = 0; i < tmpApproverData.length; i++) {
            if (!tmpApproverData[i]) {
              this.form.approverDataCustomParameter[i] = new ApproverDataToJson();
            } else {
              const tmpUser = new ApproverDataToJson();
              this.form.approverDataCustomParameter[i] = tmpUser;
              const username = tmpApproverData[i].username;
              if (username) {
                this.userService.getAdUsers([username]).subscribe({
                  next: (approver) => {
                    tmpUser.users = approver;
                  },
                  error: (error) => {
                    this.browserMessagesService.showHttpError(error, 'Daten zu den Genehmigern konnten nicht geladen werden');
                  }
                });
              }
              const deputyUsername = tmpApproverData[i].deputyUsername;
              if (deputyUsername) {
                this.userService.getAdUsers([deputyUsername]).subscribe({
                  next: (approver) => {
                    tmpUser.deputyUsers = approver;
                  },
                  error: (error) => {
                    this.browserMessagesService.showHttpError(error, 'Daten zu den Genehmigern konnten nicht geladen werden');
                  }
                });
              }
            }
          }
          this.form.dataFieldsCustomParameter = this.form.dataFields ? JSON.parse(this.form.dataFields) : [''];
          if (this.form.fields) {
            for (const field of this.form.fields) {
              field.fieldCustomParameter = JSON.parse(field.definition ?? '{}');
            }
          }
          this.edit = true;
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Formular konnte nicht geladen werden');
        }
      });
    }
  }

  declineProcess(): void {
    this.processApproval(false);
  }

  acceptProcess(): void {
    this.processApproval(true);
  }

  setResubmit(): void {
    if (this.id) {
      this.processService.resubmitProcess(new BodyResubmit(this.resubmit), this.id).subscribe({
        next: () => {
          this.closeModalResubmit();
          this.resubmit = '';
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht auf Wiedervorlage gesetzt werden');
        }
      });
    }
  }

  removeResubmit(): void {
    if (this.id) {
      this.processService.resubmitProcess(new BodyResubmit(null), this.id).subscribe({
        next: () => {
          this.closeModalResubmit();
          this.resubmit = '';
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht auf Wiedervorlage gesetzt werden');
        }
      });
    }
  }

  setInProgressProcessWithAttachment(): void {
    if (this.id) {
      if (this.fileName && this.mimeType && this.data) {
        this.processService.createAttachment(new BodyProcessAttachment(this.fileName, this.mimeType, this.data), this.id).subscribe({
          next: () => {
            this.setInProgressProcess();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Anhang konnte nicht hochgeladen werden');
          }
        });
      } else {
        this.setInProgressProcess();
      }
    }
  }

  setInProgressProcess(): void {
    if (this.id) {
      this.processService.progressProcess(new BodyProcessStatus(ProcessStatus.IN_PROGRESS, this.comment), this.id).subscribe({
        next: () => {
          this.closeModalOperator();
          this.comment = '';
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht in Bearbeitung gesetzt werden');
        }
      });
    }
  }

  setFinishedWithAttachment(): void {
    if (this.id) {
      if (this.fileName && this.mimeType && this.data) {
        this.processService.createAttachment(new BodyProcessAttachment(this.fileName, this.mimeType, this.data), this.id).subscribe({
          next: () => {
            this.setFinished();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Anhang konnte nicht hochgeladen werden');
          }
        });
      } else {
        this.setFinished();
      }
    }
  }

  setFinished(): void {
    if (this.id) {
      this.processService.progressProcess(new BodyProcessStatus(ProcessStatus.FINISHED, this.comment), this.id).subscribe({
        next: () => {
          this.closeModalOperator();
          this.comment = '';
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht fertiggestellt werden');
        }
      });
    }
  }

  setRejectedWithAttachment(): void {
    if (this.id) {
      if (this.fileName && this.mimeType && this.data) {
        this.processService.createAttachment(new BodyProcessAttachment(this.fileName, this.mimeType, this.data), this.id).subscribe({
          next: () => {
            this.setRejected();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Anhang konnte nicht hochgeladen werden');
          }
        });
      } else {
        this.setRejected();
      }
    }
  }

  setRejected(): void {
    if (this.id) {
      this.processService.progressProcess(new BodyProcessStatus(ProcessStatus.REJECTED, this.comment), this.id).subscribe({
        next: () => {
          this.closeModalReject();
          this.comment = '';
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht zurückgewiesen werden');
        }
      });
    }
  }

  createAttachment(): void {
    if (this.id && this.fileName && this.mimeType && this.data) {
      this.processService.createAttachment(new BodyProcessAttachment(this.fileName, this.mimeType, this.data), this.id).subscribe({
        next: () => {
          this.closeModalAttachment();
          this.getProcess();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Anhang konnte nicht hochgeladen werden');
        }
      });
    }
  }

  processApproval(approve: boolean): void {
    if (this.id) {
      this.processService.approveProcess(this.isActiveApproval
        .map(approval => approval.approverType ? new BodyApproval(approve, approval.approverType, this.comment) : undefined).filter(o => o) as BodyApproval[], this.id)
        .subscribe({
          next: () => {
            this.closeModalApprove();
            this.comment = '';
            this.getProcess();
            this.processService.subject.next(null);
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht genehmigt/abgelehnt werden.');
          }
        });
    }
  }

  checkActiveApproval(): void {
    this.isActiveApproval = [];
    if (this.process?.approvals && this.process.status === 'SENT') {
      for (const approval of this.process.approvals) {
        if (approval.approved === Approved.PENDING && (approval.username === this.webAuthService.authUser?.username || approval.deputyUsername === this.webAuthService.authUser?.username || this.employee?.admin)) {
          this.isActiveApproval.push(approval);
          if (approval.username === this.webAuthService.authUser?.username) {
            for (const furtherApproval of this.process.approvals) {
              if (furtherApproval.approved === 'PENDING' && approval.username === furtherApproval.username && approval.approverType !== furtherApproval.approverType && approval.forInformationOnly === furtherApproval.forInformationOnly) {
                this.isActiveApproval.push(furtherApproval);
              }
            }
          }
          break;
        }
      }
    }
  }

  openModalApprove(decline: boolean): void {
    this.decline = decline;
    const ngModalApprove = this.ngModalApprove();
    if (ngModalApprove) {
      this.ngModalServiceApprove = this.modalService.open(ngModalApprove, { centered: true, backdrop: 'static' });
    }
  }

  closeModalApprove(): void {
    this.ngModalServiceApprove?.close();
  }

  openModalResubmit(): void {
    this.resubmit = '';
    const ngModalResubmit = this.ngModalResubmit();
    if (ngModalResubmit) {
      this.ngModalServiceResubmit = this.modalService.open(ngModalResubmit, { centered: true, backdrop: 'static' });
    }
  }

  closeModalResubmit(): void {
    this.ngModalServiceResubmit?.close();
  }

  openModalOperator(): void {
    this.fileName = undefined;
    this.mimeType = undefined;
    this.data = undefined;
    const ngModalOperator = this.ngModalOperator();
    if (ngModalOperator) {
      this.ngModalServiceOperator = this.modalService.open(ngModalOperator, { centered: true, backdrop: 'static' });
    }
  }

  closeModalOperator(): void {
    this.ngModalServiceOperator?.close();
  }

  openModalReject(): void {
    this.fileName = undefined;
    this.mimeType = undefined;
    this.data = undefined;
    const ngModalReject = this.ngModalReject();
    if (ngModalReject) {
      this.ngModalServiceReject = this.modalService.open(ngModalReject, { centered: true, backdrop: 'static' });
    }
  }

  closeModalReject(): void {
    this.ngModalServiceReject?.close();
  }

  openModalAttachment(): void {
    this.fileName = undefined;
    this.mimeType = undefined;
    this.data = undefined;
    const ngModalAttachment = this.ngModalAttachment();
    if (ngModalAttachment) {
      this.ngModalServiceAttachment = this.modalService.open(ngModalAttachment, { centered: true, backdrop: 'static' });
    }
  }

  closeModalAttachment(): void {
    this.ngModalServiceAttachment?.close();
  }

  editProcess(): void {
    this.getForm();
  }

  cancelProcess(): void {
    if (this.process?.id) {
      if (this.process.status === 'APPROVED') {
        this.processService.cancelProcessAfterApproved(this.process.id).subscribe({
          next: () => {
            this.closeModalCancel();
            this.getProcess();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht storniert werden');
          }
        });
      } else {
        this.processService.cancelProcess(this.process.id).subscribe({
          next: () => {
            this.closeModalCancel();
            this.getProcess();
          },
          error: (error) => {
            this.browserMessagesService.showHttpError(error, 'Antrag konnte nicht storniert werden');
          }
        });
      }
    }
  }

  openModalCancel(): void {
    const ngModalCancel = this.ngModalCancel();
    if (ngModalCancel) {
      this.ngModalServiceCancel = this.modalService.open(ngModalCancel, { centered: true, backdrop: 'static' });
    }
  }

  closeModalCancel(): void {
    this.ngModalServiceCancel?.close();
  }

  deleteProcess(): void {
    if (this.process?.id) {
      this.processService.deleteProcess(this.process.id).subscribe({
        next: () => {
          this.closeModal();
          this.router.navigateByUrl('/my-forms').then();
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Entwurf konnte nicht gelöscht werden');
        }
      });
    }
  }

  openModal(): void {
    const ngModal = this.ngModal();
    if (ngModal) {
      this.ngModalService = this.modalService.open(ngModal, { centered: true, backdrop: 'static' });
    }
  }

  closeModal(): void {
    this.ngModalService?.close();
  }

  openModalPrivacyPolicy(): void {
    const ngModalPrivacyPolicy = this.ngModalPrivacyPolicy();
    if (ngModalPrivacyPolicy) {
      this.ngModalServicePrivacyPolicy = this.modalService.open(ngModalPrivacyPolicy, { centered: true, size: 'xl' });
    }
  }

  closeModalPrivacyPolicy(): void {
    this.ngModalServicePrivacyPolicy?.close();
  }

  createPdf(): void {
    if (this.process?.id) {
      this.processService.createPdf(this.process.id).subscribe({
        next: (data) => {
          this.exportService.createDownloadableFileFromHttpResponse(data, 'antrag.pdf');
        },
        error: (error: HttpErrorResponse) => {
          this.browserMessagesService.showHttpError(error, 'Fehler');
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileSelected(event: any): void {
    this.fileName = event.target.files[0].name.normalize('NFC');
    this.mimeType = event.target.files[0].type;
    // eslint-disable-next-line no-console
    console.log(this.fileName);
    // eslint-disable-next-line no-console
    console.log(this.mimeType);
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    reader.onload = (): void => {
      if (reader.result) {
        this.data = reader.result.toString();
        const i = this.data.indexOf('base64,');
        this.data = this.data.substring(i + 7);
        if (this.data.length > 13981013) {
          this.fileError = 'Die Datei darf maximal 10 MB groß sein.';
        }
      }
    };
  }

  failTransmission(): void {
    if (this.id) {
      this.processService.failTransmission(this.id).subscribe({
        next: () => {
          this.getProcess();
          this.processService.subject.next(null);
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Übermittlung konnte nicht abgebrochen werden');
        }
      });
    }
  }

  protected readonly ApproverType = ApproverType;
  protected readonly ProcessStatus = ProcessStatus;
  protected readonly ActionType = ActionType;
}
